import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { fmtDateTime } from "@/lib/utils2";

/**
 * In-app notification bell.
 *  - Polls /api/notifications every 60s
 *  - Shows unread count badge
 *  - Click item → mark read + jump to related request (admin or supervisor route)
 */
export default function NotificationBell({ isAdmin }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 50 } });
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Refresh when the dropdown opens to ensure newest data.
  useEffect(() => { if (open) load(); }, [open]);

  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read");
    await load();
  };

  const openItem = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_) { /* non-fatal */ }
    }
    setOpen(false);
    if (n.related_request_id) {
      // Check the request still exists before navigating — saves the user a confusing blank dialog.
      try {
        await api.get(`/holiday-requests/${n.related_request_id}`);
      } catch (e) {
        const { toast } = await import("sonner");
        toast.message("That request has been deleted or moved.");
        await load();
        return;
      }
      const dest = isAdmin ? "/admin" : "/supervisor";
      navigate(`${dest}?request=${encodeURIComponent(n.related_request_id)}`);
    }
    await load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="notif-bell"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span
              data-testid="notif-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0" data-testid="notif-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="font-heading font-semibold text-sm">Notifications</div>
          <Button
            data-testid="notif-mark-all-read"
            size="sm" variant="ghost"
            disabled={data.unread === 0}
            onClick={markAllRead}
            className="gap-1.5 h-7 text-xs"
          ><Check className="w-3 h-3"/> Mark all read</Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {data.items.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">No notifications yet.</div>
          )}
          {data.items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              onClick={() => openItem(n)}
              className={`w-full text-left px-3 py-2.5 border-b border-border/60 last:border-b-0 transition-colors ${
                n.is_read ? "bg-[#1E1E1E] hover:bg-[#141414]" : "bg-[#141414] hover:bg-[#141414]"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium truncate" data-testid={`notif-item-title-${n.id}`}>{n.title}</div>
                {!n.is_read && (
                  <span className="mt-1 inline-block w-2 h-2 rounded-full bg-blue-500 shrink-0" aria-label="unread"/>
                )}
              </div>
              {n.body && (
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>
              )}
              <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground/80 mt-1">
                {fmtDateTime(n.created_at)} · {n.type.replace(/_/g, " ")}
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
